import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ClinicUserClinicFragment, ClinicUserFragment } from 'shared/types/graphql';
import { env } from 'env';
import { appInfo, useTracking } from 'shared/providers/TrackingProvider';

interface IProps {
  clinicUser: ClinicUserFragment | null;
  currentClinic: ClinicUserClinicFragment | null;
}

const useSentry = ({ clinicUser, currentClinic }: IProps): void => {
  const { logRocketSessionUrl } = useTracking();
  useEffect(() => {
    //temporarily disabling sentry for development
    //as we discuss the benefits / costs of using it
    if (env.REACT_APP_PRODUCTION === 'true' && env.REACT_APP_SENTRY_URL && env.REACT_APP_SENTRY_ENVIRONMENT) {
      const replay = new Sentry.Replay({
        blockAllMedia: false,
        maskAllText: false,
      });
      Sentry.init({
        dsn: env.REACT_APP_SENTRY_URL || '',
        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: 0.1,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
        integrations: [new Integrations.BrowserTracing(), replay],
        tracesSampleRate: 1.0,
        environment: env.REACT_APP_SENTRY_ENVIRONMENT || 'Development',
        ignoreErrors: [
          'Non-Error promise rejection captured',
          // LogRocket producing an error with zendesk widget
          // since they use webWidget and we have moved to "messenger"
          // to implement ZD chatbot, this error is no longer relevant
          /Method webWidget:on\.open does not exist.*$/gms,
        ],
        release: `${appInfo.version}`,
        denyUrls: [
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
        ],
      });
      return (): void => {
        const flush = replay.flush();
        if (flush) {
          flush.then(() => {
            replay.stop();
          });
        }
      };
    }
  }, []);
  useEffect(() => {
    //temporarily disabling sentry for development
    //as we discuss the benefits / costs of using it
    if (env.REACT_APP_PRODUCTION === 'true') {
      if (clinicUser) {
        Sentry.setUser({
          id: clinicUser.id,
          email: clinicUser.email,
          clinic: currentClinic?.name,
          clinicId: currentClinic?.id,
        });
      }
      try {
        Sentry.configureScope((scope) => {
          if (!scope.setExtra) return;
          scope.setExtra('sessionURL', logRocketSessionUrl);
        });
      } catch (e) {
        console.error('Could not get LogRocket sessionUrl');
      }
    }
  }, [clinicUser, currentClinic, logRocketSessionUrl]);
};

export default useSentry;
